import * as React from 'react';

const DropdownMenuContext = React.createContext({});

export function DropdownMenu({ children }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <DropdownMenuContext.Provider value={{ open, setOpen }}>
      <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
        {children}
      </div>
    </DropdownMenuContext.Provider>
  );
}

export function DropdownMenuTrigger({ children, asChild, className }) {
  const { setOpen } = React.useContext(DropdownMenuContext);
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      onClick: (e) => { children.props.onClick?.(e); setOpen((o) => !o); },
    });
  }
  return <div className={className} onClick={() => setOpen((o) => !o)}>{children}</div>;
}

export function DropdownMenuContent({ children, className, align = 'start', sideOffset }) {
  const { open } = React.useContext(DropdownMenuContext);
  if (!open) return null;
  const alignStyle = align === 'end' ? { right: 0 } : { left: 0 };
  return (
    <div
      className={className}
      style={{
        position: 'absolute',
        top: '100%',
        ...alignStyle,
        zIndex: 9999,
        marginTop: sideOffset ?? 4,
        background: '#1a1a2e',
        border: '1px solid #333',
        borderRadius: 6,
        padding: '4px 0',
        minWidth: 160,
      }}
    >
      {children}
    </div>
  );
}

export const DropdownMenuItem = React.forwardRef(({ children, onClick, className, inset, ...props }, ref) => {
  const { setOpen } = React.useContext(DropdownMenuContext);
  return (
    <div
      ref={ref}
      className={className}
      onClick={() => { onClick?.(); setOpen(false); }}
      style={{ padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}
      {...props}
    >
      {children}
    </div>
  );
});

export const DropdownMenuSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={className} style={{ borderTop: '1px solid #333', margin: '4px 0' }} {...props} />
));

export const DropdownMenuLabel = React.forwardRef(({ children, className, inset, ...props }, ref) => (
  <div ref={ref} className={className} style={{ padding: '4px 12px', fontSize: 11, opacity: 0.5 }} {...props}>{children}</div>
));

export function DropdownMenuGroup({ children }) {
  return <div>{children}</div>;
}

export function DropdownMenuPortal({ children }) {
  return <>{children}</>;
}

export function DropdownMenuSub({ children }) {
  return <div>{children}</div>;
}

export const DropdownMenuSubTrigger = React.forwardRef(({ children, className, inset, ...props }, ref) => (
  <div ref={ref} className={className} style={{ padding: '6px 12px', fontSize: 13 }} {...props}>{children}</div>
));

export const DropdownMenuSubContent = React.forwardRef(({ children, className, ...props }, ref) => (
  <div ref={ref} className={className} {...props}>{children}</div>
));

export const DropdownMenuCheckboxItem = React.forwardRef(({ children, checked, onCheckedChange, className, ...props }, ref) => (
  <div ref={ref} className={className} onClick={() => onCheckedChange?.(!checked)} style={{ padding: '6px 12px', cursor: 'pointer', fontSize: 13 }} {...props}>
    {checked ? '✓ ' : '  '}{children}
  </div>
));

export function DropdownMenuRadioGroup({ children, value, onValueChange }) {
  return <div>{children}</div>;
}

export const DropdownMenuRadioItem = React.forwardRef(({ children, value, className, ...props }, ref) => (
  <div ref={ref} className={className} style={{ padding: '6px 12px', fontSize: 13 }} {...props}>{children}</div>
));

export function DropdownMenuShortcut({ children, className }) {
  return <span className={className} style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.5 }}>{children}</span>;
}
